import { useRef, useEffect } from 'react';
import { css } from '@emotion/react';
import { getLuminance } from 'color2k';
import { theme } from '@frontend/theme';
import { CheckIconSmall, NakedButton } from '@frontend/design-system';

interface PaletteProps {
  selectedColor: string;
  onChange?: (color: string) => void;
  close?: () => void;
  palette: string[];
  gridColumns?: number;
}

interface ColorButtonProps {
  color: string;
  isSelected: boolean;
  onChange?: (selectedColor: string) => void;
  close: () => void;
  autoFocus?: boolean;
}

export const Palette = ({ selectedColor, onChange, close, palette, gridColumns }: PaletteProps) => {
  return (
    <div css={paletteGridStyle(gridColumns)}>
      {palette.map((eventBackgroundColor) => {
        const isSelected = eventBackgroundColor.toUpperCase() === selectedColor.toUpperCase();
        return (
          <ColorButton
            key={eventBackgroundColor}
            color={eventBackgroundColor}
            isSelected={isSelected}
            autoFocus={isSelected}
            onChange={onChange}
            close={close ? close : () => {}}
          />
        );
      })}
    </div>
  );
};

const ColorButton = ({ color, isSelected, onChange, close, autoFocus }: ColorButtonProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (autoFocus && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [autoFocus]);

  return (
    <NakedButton
      ref={buttonRef}
      onClick={() => {
        onChange?.(color);
        close?.();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onChange?.(color);
          close?.();
        }
      }}
      css={colorButtonStyle}
    >
      <span css={colorButtonSpanStyle(color)}>
        {isSelected && <CheckIconSmall color={getLuminance(color) > 0.7 ? 'default' : 'white'} />}
      </span>
    </NakedButton>
  );
};

const paletteGridStyle = (gridColumns?: number) =>
  css({
    display: 'grid',
    gridGap: theme.spacing(1),
    gridTemplateColumns: `repeat(${gridColumns ?? 5}, 1fr)`,
    gridAutoRows: 'max-content',
    width: '100%',
    padding: theme.spacing(0.5),
  });

const colorButtonStyle = css({
  position: 'relative',
  borderRadius: theme.borderRadius.full,
  width: 20,
  height: 20,

  ':focus': {
    outline: 'none',
  },

  ':focus span::after': {
    opacity: 1,
  },
});

const colorButtonSpanStyle = (color: string) =>
  css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.borderRadius.full,
    position: 'relative',
    background: color,
    height: 20,
    width: 20,
    border: `1px solid ${theme.colors.neutral30}`,
    '::after': {
      content: '""',
      height: 'calc(100% + 10px)',
      width: 'calc(100% + 10px)',
      display: 'block',
      background: 'transparent',
      zIndex: -1,
      border: `2px solid ${theme.colors.primary50}`,
      borderRadius: theme.borderRadius.full,
      opacity: 0,
      pointerEvents: 'none',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      transition: 'opacity 150ms ease-out',
    },
  });
